/**
 * **The damage application — S9b (the hit arm) and S9c (the Reliable-floor arm).** Two of the
 * three arms of `attack.ts`'s `resolveSingleTarget` damage chain, ending just before the Lock On
 * clear ({@link import("./weaponConsume").consumeStatusCosts}).
 *
 * The chain's shared preamble (the effective armor, the Exposed double, and the
 * tech / no-damage-melee skip) stays in `resolveSingleTarget` — BOTH arms read it, so it belongs
 * to neither. So does the chain's **third** arm, the plain-miss `tg_heat_self` cost: it is not a
 * damage application at all (no `damage` step, no mitigation), just the fire cost a miss with no
 * Reliable floor still owes, so it stays inline rather than becoming a third stage here.
 *
 * **{@link applyReliableFloorDamage} (S9c) is the miss-side arm** and is deliberately NOT a
 * degenerate case of the hit arm: no dice are drawn (the floor is a fixed N), and its damage window
 * (`gatherReliableMissReaction`) opens only the target's authored offers (Rebound: "takes damage
 * from a ranged attack"), not Brace (RAW "hit by an attack") or a protector's, so there is no
 * protector link, no per-instance loop and no rider — one flat instance, mitigated once. The
 * target's automatic `on:"damage"` clauses run in that same window, and its settled `match.dealt`
 * clauses after routing, as on a hit. The Sniper's Mark substitution arm of {@link applyHitDamage}
 * runs the automatic clauses too (`gatherDamageNotification`) but opens no offer: it deals no damage
 * a Resistance could touch. What it DOES share with the
 * hit arm is the mitigate/immunity/type-routing shape and the Heat (Self) cost, which is why the
 * two live in one module. Its Heat (Self) cascade is its own: unlike the hit arm's, it has no
 * Overkill heat to fold in, because no dice were rolled to reroll.
 *
 * {@link applyHitDamage} runs, in this exact order:
 *
 * 0. the structure-damage SUBSTITUTION arm (Sniper's Mark) — an early return that replaces the
 *    whole stage below it with one structure point taken through `resolveDamageCascade`'s
 *    `substituteStructurePoint` mode (plus the Heat (Self) fire cost). Inert unless the S3 target
 *    fold set `substituteStructureDamage`; see its own comment at the top of the function for the
 *    four things it skips and the two gates that refuse it;
 * 1. the up-front per-instance draw ({@link rollDamageInstances}) — primary first, then every
 *    rider in frozen source order, ALL dice drawn before anything is mitigated;
 * 2. the damage-interception reaction window (`gatherReaction`) — **exit 1**, whose caught signal
 *    is dressed with the both-ways {@link previewIncomingDamage} preview;
 * 3. the protector-link resolution (Argonaut Shield's split / Intercede's redirect), once for the
 *    whole attack rather than per instance;
 * 4. the sequential per-instance mitigation + type routing ({@link applyDamageInstance}), inside
 *    ONE try/catch that owns **exit 2** (a System Trauma prompt raised mid-loop);
 * 5. the combined Overkill + Heat (Self) cascade onto the wielder;
 * 6. the Brace consequence steps.
 *
 * **{@link applyDamageInstance} THROWS rather than returning a halt, and the loop's own
 * try/catch stays here in {@link applyHitDamage}.** That is deliberate: the catch is ONE window
 * around the whole loop (a mid-loop abort discards every partially-built step, which is safe only
 * because the resolution is iterate-pure), so turning the per-instance helper into a
 * halt-returning stage would force the loop to re-implement that window per instance and change
 * where the abort lands.
 *
 * Both stages RETURN their `steps` and `warnings` rather than pushing onto the caller's
 * accumulators (the same purity discipline {@link import("./attackModifiers").foldAttackModifiers}
 * follows), as {@link applyHitDamage} does with the
 * reactor entries the caller merges into its own `reactionSpentBy`. Nothing between a stage's
 * first push and its last reads either accumulator, so splicing once at the seam is
 * byte-identical to the inline pushes.
 *
 * **All three of this module's exits DEFAULT their recipient to the TARGET** when the caught
 * signal names nobody — the opposite actor from the attack outcome's raise in `attack.ts`,
 * which falls back to the attacker. Every prompt here is the defender's to answer: the
 * interception window asks the target whether to Brace, and either damage path's System Trauma
 * prompt asks the target which system it loses. Neither the differential corpus nor
 * `bun test packages/rules` protects a flipped fallback actor (measured — see the tracker's
 * Gotchas), so the actor on those three lines is checked by reading, not by a gate.
 */

import { reactionLinked } from "../reactionLink";
import { applyResolved, hasStructureTrack, type Combatant, type DamageStep, type DamageType, type EncounterState, type ResolvedStep, type WeaponProfile } from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import type { DiceSource } from "../../dice";
import type { Cause, EventLoop, Replacement } from "../events/loop";
import { drawRoll } from "../events/rollEvents";
import { NeedsDecisionSignal, foldedResistance, immuneToDamage, type ForcedAttackRequest, type LoadedOverlay } from "../effects";
import { nonAttackArmor, nonAttackResistant } from "../effects/nonAttackResistance";
import { gatherDamageNotification, gatherReaction, gatherReliableMissReaction, gatherSettledDamageOutcome, type ReactionContext, type ReactionOutcome, type SettledDamageOutcome } from "../reactionRegistry";
import { resolveHeatCascade } from "../tables";
import { selfCheckContext } from "../selfCheck";
import type { SharedPoolInject } from "./area";
import { isPhysical, rollInstanceDice } from "./damage";
import { dealDamagePacket, landDamageInstance, mitigateDamageInstance, type LandedInstance } from "./damagePacket";
import { raiseDamagePacket, type DamagePacketEvent } from "../events/damageEvents";
import { interactiveFire, isNeedsDecision, type EffectsConfig, type NeedsDecision } from "./types";

/** Where a damage stage raises its `roll` events: the intent's loop, who rolls, and what causes the roll (the attack's outcome). */
export type DamageRollEvents = {
  loop: EventLoop | undefined;
  roller: string;
  cause?: Cause;
  /** The damage roll's replacements (Brutal's maximize), matched on the outcome; each instance's roll is offered to them. */
  replacements?: readonly Replacement[];
};

/** One damage instance's settled draw — the shape {@link rollDamageInstances} produces per entry. */
export type InstanceRoll = {
  dtype: DamageType;
  dice: number[];
  damageBonus: number;
  overkillHeat: number;
  preMitigation: number;
  /** The weapon's Reliable X, set only when it lifted this (primary) instance's roll up to X. */
  reliable?: number;
  /**
   * The damage die's side count (`npc-weapon-rider-damage`-shaped per-instance carry of the
   * `dice-overlay-adhoc-roll` `sides` field — for the dice overlay's mesh choice, d3 vs d6).
   * `undefined` for a dice-less instance (flat/count-0, e.g. a `t_walking_armory` rider or
   * Reliable's floor) — mirrors the field's own optionality on {@link DamageStep}.
   */
  sides?: number;
};

/**
 * One damage instance's settled type: an attacker-side override wins outright, a `variable` entry
 * takes S4's answer (else the primary's settled type), and a concrete entry keeps its own. Shared
 * by {@link rollDamageInstances} and the pre-roll forecast (`attackForecast.ts`).
 */
export function instanceDamageType(opts: {
  entry: WeaponProfile["damage"];
  overrideDamageType: DamageType | undefined;
  resolvedVariableType: DamageType | undefined;
  dtype: DamageType;
}): DamageType {
  const { entry, overrideDamageType, resolvedVariableType, dtype } = opts;
  return overrideDamageType ?? (entry.type === "variable" ? (resolvedVariableType ?? dtype) : entry.type);
}

/**
 * **S9b's first sub-helper — the up-front draw.** Rolls EVERY instance's dice, primary first then
 * riders in source order (decision 3) — crit doubles rider dice too (decision 5, the SAME
 * `keepHighest`/overkill machinery the primary already used, since `weapon.overkill` is a
 * weapon-wide tag every instance shares); the area fan-out's once-rolled shared pool (decision 8)
 * is picked per instance by `isCrit` instead of drawing. This is ALSO decision 7's draw-order pin:
 * the reaction window in {@link applyHitDamage} opens only AFTER every draw here, so a
 * `needs_decision` re-resolve REPLAYS this whole prefix (primary dice, then every rider's) rather
 * than re-drawing it — the same recorded-then-replayed discipline `resolve.diceReplay.test.ts`
 * pins for Lock On's post-dice prompt, now proven for riders by `resolve.riderDamage.test.ts`'s
 * replay case.
 */
export function rollDamageInstances(opts: {
  /** The SELECTED firing profile — its `damage` is the primary instance, its `riders` the rest. */
  weapon: WeaponProfile;
  isCrit: boolean;
  dice: DiceSource;
  /** The area fan-out's shared pool, index-aligned with the instance list; `undefined` off an area weapon. */
  sharedDamage: SharedPoolInject | undefined;
  /** The attacker-side `override_attack_damage_type` facet, when one is active. */
  overrideDamageType: DamageType | undefined;
  /** S4's settled answer to a `variable` weapon's type prompt. */
  resolvedVariableType: DamageType | undefined;
  /** S4's settled primary damage type — the fallback a `variable` instance lands on. */
  dtype: DamageType;
  /** S3's Exposed facet (A3) — doubles PHYSICAL pre-mitigation damage, per instance. */
  doubleDamage: boolean;
  /** Where each instance's dice are raised as a `roll` event: the intent's loop, the roller and the outcome that causes them. */
  events?: DamageRollEvents;
}): InstanceRoll[] {
  const { weapon, isCrit, dice, sharedDamage, overrideDamageType, resolvedVariableType, dtype, doubleDamage, events } = opts;
  // The full damage instance list, in frozen source order (`npc-weapon-rider-damage` Phase 3,
  // decisions 1+3): the primary owns to-hit/crit/Overkill/Reliable/the area pool; every rider
  // rides along as its own sequential per-instance resolution — own dice, own dtype routing, own
  // `DamageStep`.
  const instances = [weapon.damage, ...(weapon.riders ?? [])];

  return instances.map((entry, idx) => {
    // `override_attack_damage_type` (P2): an override wins OUTRIGHT for every instance
    // (primary AND riders) — it recolors "the attack's own [damage type]" (t_walking_armory:1's
    // own RAW text), not just the primary's frozen field. Falls through to each instance's
    // OWN type (its `variable` choice, or its concrete frozen type) exactly as before when
    // no override is active — this is additive, not a reshape of the pre-P2 per-instance path.
    const instDtype = instanceDamageType({ entry, overrideDamageType, resolvedVariableType, dtype });
    const inject = sharedDamage?.[idx];
    // An instance with dice of its own raises a `roll`, which its replacements may rewrite (Brutal's
    // maximize draws nothing). One that reads the area fan-out's shared pool was raised once, up
    // front, with that pool, before any outcome existed, so no maximize reaches it.
    const r =
      events === undefined || inject !== undefined || entry.count === 0
        ? rollInstanceDice({ entry, isCrit, overkill: weapon.overkill, dice, inject })
        : drawRoll({
            loop: events.loop,
            dice,
            ...(events.cause !== undefined ? { cause: events.cause } : {}),
            roll: {
              kind: "roll",
              roller: events.roller,
              purpose: { kind: "damage", instance: idx, crit: isCrit },
              spec: {
                dice: [{ count: isCrit ? entry.count * 2 : entry.count, sides: entry.sides }],
                ...(isCrit ? { keepHighest: entry.count } : {}),
                accuracy: 0,
                ...(weapon.overkill ? { rerollOnes: true as const } : {}),
              },
            },
            replacements: events.replacements ?? [],
            draw: (d, roll) => rollInstanceDice({ entry, isCrit, overkill: roll.spec.rerollOnes === true, dice: d, inject, maximize: roll.spec.maximize === true }),
            facts: (rolled) => (weapon.overkill ? { overkillHeat: rolled.overkillHeat } : {}),
          }).result;
    const roll = r.dice.reduce((a, b) => a + b, 0) + r.damageBonus;
    // Reliable X (Core, the RELIABLE tag: "always does X damage, even if it misses its target or rolls less
    // damage") floors the primary's rolled total on a hit too; mitigation then applies to the
    // lifted total, as it does to the miss arm's floor ({@link applyReliableFloorDamage}).
    const floored = idx === 0 && roll < weapon.reliable;
    const rolled = floored ? weapon.reliable : roll;
    // Exposed (A3) doubles physical pre-mitigation damage — per instance (decision 3): a Burn
    // rider on an Energy primary is never doubled even though the primary is.
    const instDoublePhysical = doubleDamage && isPhysical(instDtype);
    return {
      dtype: instDtype,
      dice: r.dice,
      damageBonus: r.damageBonus,
      overkillHeat: r.overkillHeat,
      preMitigation: instDoublePhysical ? rolled * 2 : rolled,
      ...(floored ? { reliable: weapon.reliable } : {}),
      // Record the die size so the overlay picks the right mesh (d3 vs d6). Only meaningful
      // when dice were actually rolled — a flat-damage instance (count 0) rolls none and the
      // overlay shows no damage dice for it, so `sides` stays unset there.
      sides: entry.count > 0 ? entry.sides : undefined,
    };
  });
}

/** The both-ways damage preview {@link previewIncomingDamage} dresses the interception halt with. */
type IncomingDamagePreview = NonNullable<NeedsDecision["incomingDamage"]>;

/**
 * **S9b's PRIVATE preview sub-helper** (`resolve-single-target-decomposition` D2 — every input it
 * needs is already in {@link applyHitDamage}'s own scope, so it never leaves this module).
 *
 * Surface the incoming damage on the prompt (combat-resolution-ui / reaction-decision-surface):
 * the damage is already rolled here, so the decider sees how much they'd take before choosing.
 * Mitigate both ways — declined (armor only) vs reacted (Brace's Resistance) — PER INSTANCE
 * (decision 7), then sum for the top-level figures; `breakdown` carries the per-instance detail.
 */
function previewIncomingDamage(opts: {
  state: EncounterState;
  target: Combatant;
  overlay: LoadedOverlay | undefined;
  /** The SELECTED firing profile — its `ap` flattens armor in both arms of the preview. */
  weapon: WeaponProfile;
  /** The chain preamble's effective target armor (0 when Shredded or AP flattened it). */
  effectiveArmor: number;
  /** S3's Shredded facet — suppresses the reacted arm's Resistance, per RAW p.78. */
  ignoreTargetArmor: boolean;
  /** Each instance's type and pre-mitigation total — the Reliable floor passes its one flat instance. */
  rolls: Pick<InstanceRoll, "dtype" | "preMitigation">[];
}): IncomingDamagePreview {
  const { state, target, overlay, weapon, effectiveArmor, ignoreTargetArmor, rolls } = opts;
  // Both arms run the packet's own mitigation (`mitigateDamageInstance`), Armor spent once across
  // the instances in order as the resolution spends it (BC-071).
  const arm = (reacted: boolean) => {
    let armor = weapon.ap ? 0 : effectiveArmor;
    return rolls.map((r) => {
      const m = mitigateDamageInstance({ target, instance: { dtype: r.dtype, amount: r.preMitigation }, ap: weapon.ap, armor, resistant: reacted && !ignoreTargetArmor, reduce: 0, immune: false });
      armor -= m.armorUsed;
      return m.witness.final;
    });
  };
  const declined = arm(false);
  // `status-catalog-completion` P1.3: a Shredded target gets no Resistance benefit even from a
  // reaction that would otherwise grant it (RAW p.78 "don't benefit from ARMOR or RESISTANCE") —
  // the both-ways preview must show the SAME unhalved figure Shredded forces on the real
  // resolution, so the GM's decision prompt never previews a halving the target can't actually get.
  const reacted = arm(true);
  const breakdown = rolls.map((r, i) => {
    // Immune → RAW p.68's no-op, never a to-hit/damage number ("may as well have
    // failed"): the preview shows 0 either way rather than a mitigated-but-real
    // figure (decision 8 — `reactive-defenses-content` P1).
    const rImmune = immuneToDamage({ state, targetId: target.id, dtype: r.dtype, overlay });
    return { type: r.dtype, rolled: r.preMitigation, ifDeclined: rImmune ? 0 : declined[i]!, ifReacted: rImmune ? 0 : reacted[i]! };
  });
  return {
    type: rolls[0]!.dtype,
    rolled: breakdown.reduce((a, b) => a + b.rolled, 0),
    ifDeclined: breakdown.reduce((a, b) => a + b.ifDeclined, 0),
    ifReacted: breakdown.reduce((a, b) => a + b.ifReacted, 0),
    ...(breakdown.length > 1 ? { breakdown } : {}),
  };
}

/**
 * **S9b's per-instance sub-helper — one damage instance, target share then protector share.**
 *
 * **It THROWS a {@link NeedsDecisionSignal} rather than returning one** (a structure-damage
 * cascade can raise a System Trauma prompt): the ONE try/catch that turns that into a halt wraps
 * the WHOLE loop in {@link applyHitDamage}, because aborting mid-loop discards every
 * partially-built step from every instance, not just this one's.
 *
 * `steps` and `warnings` are returned in emission order — the target's own `damage` step and its
 * routed cascade first, then the protector's. Each share's Overshield is read off the combatant
 * the caller hands it, as the earlier instances left it.
 */
export function applyDamageInstance(opts: {
  state: EncounterState;
  target: Combatant;
  intent: RequestAttack;
  /** The SELECTED firing profile — its `ap` is the mitigation input. */
  weapon: WeaponProfile;
  overlay: LoadedOverlay | undefined;
  effects: EffectsConfig | undefined;
  dice: DiceSource;
  /** The target's Armor still unspent by the packet's earlier instances (the preamble's effective Armor to start; 0 for AP). */
  armor: number;
  /** The protector's, likewise (its rating to start, 0 when Shredded or AP). */
  protectorArmor: number;
  /** The Reduce still unspent by the packet's earlier instances (the window's whole reduce to start). */
  reduce: number;
  /** S3's Shredded facet — gates the whole Resistance disjunction shut. */
  ignoreTargetArmor: boolean;
  /** S7's bound crit verdict, recorded on every emitted `damage` step. */
  isCrit: boolean;
  /** This instance's settled draw. */
  r: InstanceRoll;
  /** The damage-interception window's outcome — its `resistant`/`reduce`/split/redirect facets. */
  reaction: ReactionOutcome;
  /** The bound protector, when a split or redirect link resolved one; `undefined` otherwise. */
  protector: Combatant | undefined;
  /** The intent's event loop: the cascades' checks raise their rolls into it. */
  loop?: EventLoop;
}): { steps: ResolvedStep[]; warnings: string[]; armorUsed: number; protectorArmorUsed: number; reduced: number } {
  const { state, target, intent, weapon, overlay, effects, dice, loop } = opts;
  const { armor, protectorArmor, reduce, ignoreTargetArmor, isCrit, r, reaction, protector } = opts;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  const protectorShare = protector ? (reaction.redirect ? r.preMitigation : Math.ceil(r.preMitigation / 2)) : 0;
  const targetShare = protector ? r.preMitigation - protectorShare : r.preMitigation;
  // The witness's provenance (`resolution-narration` 1.2): this whole per-instance loop is the
  // FIRING WEAPON's own damage — primary instance and any of the weapon's own rider instances (a
  // Burn/Heat rider on the same profile) alike — never a talent/system `extra_damage` atom (that's
  // `interpretExtraDamage`'s separate `"rider"` source, effects/interpret.ts).
  const source = { kind: "weapon" as const, name: weapon.name, id: weapon.id };
  // Route the final, post-mitigation amount by damage type (`routeFinalDamage`): kinetic/energy/
  // explosive/burn drain Overshield then cascade to HP (burn also marks); heat cascades straight
  // into the Overheating/Stress track.
  const route = { dice, choices: intent.choices, ...interactiveFire(effects), selfCheck: selfCheckContext(state, overlay, { loop }), from: intent.attacker };

  // Brace's "Resistance to the triggering attack" rides the EXISTING A2 mitigate path,
  // UNIFIED (decision 5) with a folded target-side `resistance` facet (`decisions/0055`
  // seam 2 — Argonaut Shield, etc.), folded per instance's OWN dtype (RAW: heat IS
  // resistance-affected too). `status-catalog-completion` P1.3: a Shredded target
  // (`ignoreTargetArmor`) gets NO Resistance benefit from ANY source — built-in Brace or a
  // folded grant alike — so the whole disjunction is gated shut, not just the folded half.
  const resistant = !ignoreTargetArmor && (reaction.resistant || foldedResistance({ state, targetId: target.id, attackerId: intent.attacker, fromAttack: true, dtype: r.dtype, overlay }));

  // Immunity, damage scope (`decisions/0060` point 3, `reactive-defenses-content` P1,
  // decision 8): RAW p.68 — the immune target "never takes damage, even 0 damage." This
  // is a NO-OP, not a mitigated-to-zero number: the `hp_delta`/cascade/burn-mark steps
  // are suppressed ENTIRELY (never emitted), and the `damage` step itself is
  // flagged `immune: true` + a play-by-play note — nothing "applies and is ignored." Read
  // per instance's OWN dtype — a target immune to heat but not energy still takes an
  // Energy primary + Burn rider hit, with only a would-be Heat rider no-op'd.
  const immune = immuneToDamage({ state, targetId: target.id, dtype: r.dtype, overlay });
  const own = landDamageInstance({
    state,
    target,
    instance: { dtype: r.dtype, amount: targetShare, dice: r.dice, ...(r.sides !== undefined ? { sides: r.sides } : {}), bonus: r.damageBonus, crit: isCrit },
    ap: weapon.ap,
    armor,
    resistant,
    reduce,
    immune,
    scale: intent.damageScale,
    witness: { ...(r.reliable !== undefined ? { reliable: r.reliable } : {}), source },
    route,
  });
  steps.push(own.witness);
  if (immune) {
    warnings.push(`${target.name}: IMMUNE to ${r.dtype} damage — no effect (RAW p.68, never takes damage from an external source).`);
  } else {
    if (own.reduced > 0) {
      warnings.push(`${target.name}: incoming damage reduced by ${own.reduced} (Inspiring Presence) — ${own.mitigated} → ${own.witness.final}.`);
    }
    steps.push(...own.landing);
    warnings.push(...own.warnings);
  }

  // The protector's own share of THIS instance (decision 9, looped per instance by Phase 3):
  // mitigated with ITS OWN Armor/Resistance, recorded as grounded steps against the PARTNER
  // inside the attacker's resolution — so one undo refunds both sides (the crown-jewel
  // invariant this project preserves). Same damage-witness/cascade/burn shape as the target's
  // own branch, just against `protector`, emitted right after the target's own instance step.
  // In an AoE fan-out the protector may ALSO be caught as a target; each lands on the state the
  // other left (BC-023). A Shredded protector gets no Armor (the caller zeroes it) and no
  // Resistance on its share (Core p.78, R6).
  let protectorArmorUsed = 0;
  if (protector) {
    const protectorImmune = immuneToDamage({ state, targetId: protector.id, dtype: r.dtype, overlay });
    const shared = landDamageInstance({
      state,
      target: protector,
      // The protector's share is derived math, not an independent roll.
      instance: { dtype: r.dtype, amount: protectorShare, dice: [], bonus: 0, crit: isCrit },
      ap: weapon.ap,
      armor: protectorArmor,
      resistant: nonAttackResistant({ state, targetId: protector.id, attackerId: intent.attacker, fromAttack: true, dtype: r.dtype, overlay }),
      reduce: 0,
      immune: protectorImmune,
      scale: intent.damageScale,
      witness: { source },
      route,
    });
    protectorArmorUsed = shared.armorUsed;
    steps.push(shared.witness);
    if (protectorImmune) {
      warnings.push(
        `${protector.name}: IMMUNE to ${r.dtype} damage — no effect (RAW p.68, never takes damage from an external source).`,
      );
    } else {
      steps.push(...shared.landing);
      warnings.push(...shared.warnings);
    }
  }

  return { steps, warnings, armorUsed: own.armorUsed, protectorArmorUsed, reduced: own.reduced };
}

/** What {@link applyHitDamage} settles for the rest of the spine. */
export type HitDamageApplication = {
  /**
   * Every instance's damage/cascade steps in emission order, then the combined heat cascade,
   * then the Brace consequence — the caller pushes them onto its own accumulator in one go at
   * the seam, byte-identically to the inline pushes.
   */
  steps: ResolvedStep[];
  /** The same, for the advisory warnings the instances and the heat cascade raised. */
  warnings: string[];
  /**
   * The damage-window reactor(s) whose reaction the room must spend. Returned RAW rather than
   * merged, so the caller's own merge with S7's to-hit-window entries keeps the exact truthiness
   * shape the inline merge had — including that an empty merged list collapses back to
   * `undefined`.
   */
  spendReactionBy: { combatantId: string; name: string }[] | undefined;
  /**
   * Forced attacks the settled-damage pass queued (Aggression's Chain Axe swing,
   * `npc-features-fielded-roster` Phase 13.2). Absent when none; `resolveSingleTarget` carries them
   * onto `Resolved.forcedAttacks` for the referee.
   */
  forcedAttacks?: ForcedAttackRequest[];
  /** The cap uses the settled-damage pass's clauses consumed (BC-012), for the attack to raise with its own. */
  consumedCaps?: { carrier: string; key: string }[];
  /**
   * How many times heat this attack's on-hit clauses add to the target is halved: once for the
   * target's Resistance to it (a Brace, unless Shredded), once for a `damageScale: "half"` attack.
   * `fireHitTriggers` hands it to the fired clauses (`FireContext.heatHalvings`).
   */
  targetHeatHalvings?: number;
  /**
   * The target accepted a reaction that gives it Resistance to this attack (a Brace, unless
   * Shredded). `fireHitTriggers` hands it to the fired clauses (`FireContext.reactionResisted`), so
   * an on-hit `extra_damage` is halved with the weapon's own instances.
   */
  targetReactionResisted?: boolean;
  /**
   * The hit's damage-window outcome (a Brace, an accepted offer), for the Unraveler: when its hit
   * is discarded for the Reliable floor, the floor carries this reaction instead of opening a new
   * window ({@link ApplyReliableFloorDamageArgs.carriedReaction}).
   */
  reaction?: ReactionOutcome;
};

/** Args for {@link applyHitDamage} (max-params-3 discipline, `CODE_STYLE.md`). */
export type ApplyHitDamageArgs = {
  state: EncounterState;
  attacker: Combatant;
  target: Combatant;
  intent: RequestAttack;
  /** The SELECTED firing profile — its damage/riders/overkill/ap drive the whole stage. */
  weapon: WeaponProfile;
  overlay: LoadedOverlay | undefined;
  effects: EffectsConfig | undefined;
  dice: DiceSource;
  /**
   * The state the damage lands on: the loop's scratch, with every earlier step of this intent
   * folded in (an area fan-out's earlier targets, BC-023). The target and its protector take the
   * damage as it left them; every rules read keeps `state`. Omitted → `state`.
   */
  landedOn?: EncounterState;
  /** The area fan-out's shared damage pool; `undefined` for a plain single-target resolution. */
  sharedDamage: SharedPoolInject | undefined;
  /** S7's bound crit verdict. */
  isCrit: boolean;
  /** S4's settled primary damage type. */
  dtype: DamageType;
  /** S4's settled answer to a `variable` weapon's type prompt. */
  resolvedVariableType: DamageType | undefined;
  /** S3's `override_attack_damage_type` facet. */
  overrideDamageType: DamageType | undefined;
  /** S3's Exposed facet (A3). */
  doubleDamage: boolean;
  /** S3's Shredded facet — no armor, and no Resistance from any source. */
  ignoreTargetArmor: boolean;
  /** The chain preamble's effective target armor (0 when Shredded or AP flattened it). */
  effectiveArmor: number;
  /**
   * S8's Heat (Self) per-fire cost. Folded into the SAME `resolveHeatCascade` call as Overkill's
   * heat rather than cascading separately — see the call site's own comment for why two calls
   * would mis-cascade a combined cap-cross.
   */
  selfHeatAmt: number;
  /**
   * The already-computed `rangedAttack` fact (`attackFacts.ts`, `npc-features-fielded-roster`
   * Phase 7.2) — threaded onto {@link import("../reactionRegistry").ReactionContext.ranged} so
   * a reaction offer's `match: { family: "damage", ranged: true }` (Rebound's own trigger) can
   * gate on it. Never re-derived here; `attack.ts` computes it once per resolution.
   */
  rangedAttack: boolean;
  /** The already-computed `techAttack` fact, threaded onto {@link import("../reactionRegistry").ReactionContext.tech} beside `rangedAttack`. */
  techAttack: boolean;
  /**
   * **Hunker Down's bridge** (`npc-features-fielded-roster` Phase 7.3, Q3(a)) —
   * the landed outcome's `resistant` (Hunker Down's replacement, or a declared
   * `resist_pending_damage`). ORed into this stage's {@link ReactionOutcome.resistant} right after
   * {@link gatherReaction} returns (below), BEFORE any instance is mitigated — the outcome's
   * replacement was answered before the outcome landed, when no `damage` step existed yet to
   * intercept; this is the one line that makes its grant reach the `damage` step's own `resisted` flag.
   */
  toHitResistant: boolean;
  /**
   * S3's Sniper's Mark facet ({@link
   * import("../effects/atoms").ModifierContribution.substituteStructureDamage}) — when it folded
   * `true` off the TARGET's modifiers, this hit deals 1 structure damage instead of its usual
   * damage. Consumed by the substitution arm at the very top of this stage.
   */
  substituteStructureDamage: boolean;
  /**
   * **Combatants that already spent a reaction earlier in THIS resolution** — the area fan-out's
   * in-flight ledger (`area-attack-reaction-offers` Phase 2.3, D5), threaded onto this stage's
   * {@link import("../reactionRegistry").ReactionContext} so a protector bonded to two caught
   * targets is offered for the first only. See {@link
   * import("../reactionRegistry").reactionAvailable}'s doc. Absent on the single-target spine.
   */
  spentReactions?: ReadonlySet<string>;
  /** Where the damage dice are raised as `roll` events. */
  events?: DamageRollEvents;
};

/**
 * The settled-damage pass ({@link gatherSettledDamageOutcome}) for all three damage arms, with the
 * module's shared exit: a `choose` inside a settled clause's body halts to the TARGET.
 */
function settleDamage(ctx: ReactionContext, settled: { dealt: boolean; dtypes: readonly DamageType[] }): SettledDamageOutcome | NeedsDecision {
  try {
    return gatherSettledDamageOutcome(ctx, settled);
  } catch (sig) {
    if (sig instanceof NeedsDecisionSignal) {
      return { kind: "needs_decision", who: sig.who ?? ctx.target.id, promptId: sig.promptId, prompt: sig.prompt, options: sig.options, answerKind: sig.answerKind };
    }
    throw sig;
  }
}

/**
 * **S9b — apply a hit's weapon damage.** See the module doc for the stage's order of operations,
 * its two exits and why the per-instance helper throws instead of returning a halt.
 */
export function applyHitDamage(args: ApplyHitDamageArgs): HitDamageApplication | NeedsDecision {
  const { state, attacker, target, intent, weapon, overlay, effects, dice, sharedDamage } = args;
  const { isCrit, dtype, resolvedVariableType, overrideDamageType, doubleDamage } = args;
  const { ignoreTargetArmor, effectiveArmor, selfHeatAmt, rangedAttack, techAttack, toHitResistant } = args;
  const { substituteStructureDamage } = args;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  // The target's reaction context, shared by the substitution arm's notification pass and the
  // ordinary path's interception window below.
  const reactionCtx: ReactionContext = {
    state,
    attacker,
    target,
    choices: intent.choices,
    ...interactiveFire(effects),
    dice,
    overlay,
    ranged: rangedAttack,
    tech: techAttack,
    hit: true,
    ...(args.spentReactions ? { spentReactions: args.spentReactions } : {}),
    // `npc-features-fielded-roster` Phase 11.1 — Rebound's reflection re-resolves this attack;
    // a reflection itself (`reactions: false`) opens no offer.
    triggeringAttack: intent,
    ...(effects?.forced?.reactions === false ? { offersClosed: true } : {}),
  };

  // ── The structure-damage SUBSTITUTION arm (`npc-features-fielded-roster` Phase 8.1) ───────────
  // Sniper's Mark, Core p.309: "Against targets with the SNIPER'S MARK, the ANTI-MATERIEL RIFLE
  // deals 1 structure damage instead of its usual damage." The facet folded off the TARGET's own
  // modifiers at S3, so by here it is a settled fact about this hit.
  //
  // It runs FIRST, ahead of the up-front damage draw, and returns — the substitution replaces the
  // whole stage, not one of its sub-blocks. What that means, stated rather than implied:
  //
  //   - **no damage dice are drawn.** RAW replaces the damage; the table rolls nothing either.
  //   - **no damage-interception reaction window.** Settled twice over, and BOTH halves matter,
  //     because either alone would leave the other as an open question the next reader re-derives.
  //     Brace, verbatim (`reference/lancer-core-book.txt`, the BRACE entry, Core p.72): "Reaction,
  //     1/round. Trigger: You are hit by an attack and damage has been rolled. Effect: You count
  //     as having RESISTANCE to all damage, Heat, and Burn from the triggering attack…".
  //     (1) The TRIGGER never fires: a substituted hit rolls no damage, so the trigger's second
  //     clause is never satisfied. (2) Even if it fired, the EFFECT is Resistance — a halving of a
  //     damage total — and a substituted structure point is not a quantity Resistance can touch;
  //     there is no damage total here to halve. So this is not a scoping choice this arm makes:
  //     there is nothing for the window to do, on either count.
  //     The interception seam that DOES apply is the structure-loss window
  //     inside the cascade below, which is why the substitution goes through `resolveDamageCascade`
  //     rather than emitting its own `structure_delta`: a marked target with Custom Paint Job still
  //     gets its prompt.
  //   - **the target's `on:"damage"` clauses still fire** (`damage-family-attack-path-holes`): no
  //     window means no OFFERS, not no notification. The automatic clauses run through
  //     `gatherDamageNotification` ahead of the cascade, and the settled `match.dealt` clauses
  //     (Aggression) after it, reading `dealt` off the structure point actually taken (a Custom
  //     Paint Job negation reads `false`). Same envelope as the hit arm: sequenced combat only.
  //   - **no protector split/redirect, no Overshield, no Armor, no Resistance.** All four move or
  //     mitigate HP-track damage, of which there is none.
  //   - **Heat (Self) is still charged** (below): it is the cost of FIRING the weapon, not of the
  //     damage it deals. Overkill heat cannot arise — it comes from rerolled damage dice.
  //
  // Two gates, both refusing to substitute rather than guessing:
  //   - **immunity wins.** An immune target falls through to the ordinary path, whose `damage` step
  //     is emitted flagged `immune: true` with every application suppressed (RAW p.68's no-op).
  //   - **no structure track, no substitution.** A pilot/object/drone has no structure point to
  //     take, so the hit deals its damage normally and a warning names why.
  if (substituteStructureDamage && !immuneToDamage({ state, targetId: target.id, dtype, overlay })) {
    if (!hasStructureTrack(target)) {
      warnings.push(
        `${target.name}: has no STRUCTURE track, so the structure-damage substitution does not apply — ` +
          `this hit deals its damage normally.`,
      );
    } else {
      let notification: { steps: ResolvedStep[]; warnings: string[]; named?: string[] };
      let substituted: { steps: ResolvedStep[]; warnings: string[] };
      try {
        notification = gatherDamageNotification(reactionCtx);
        // The hit is a damage packet whose core rule substitutes the structure point for its damage.
        substituted = dealDamagePacket({
          packet: { kind: "damage_packet", source: attacker.id, target: target.id, instances: [], fromAttack: true, ap: weapon.ap, substituteStructure: true },
          state,
          dice,
          ...(overlay !== undefined ? { overlay } : {}),
          choices: intent.choices,
          ...interactiveFire(effects),
          selfCheck: selfCheckContext(state, overlay, { loop: args.events?.loop }),
          loop: args.events?.loop,
        });
      } catch (sig) {
        // The SAME exit shape as the per-instance loop's: the structure-loss interception window
        // (and the System Trauma row behind it), or a `choose` in a notified clause, can raise a
        // prompt, and nothing has been applied yet — the re-submitted intent re-runs the whole
        // resolution.
        if (sig instanceof NeedsDecisionSignal) {
          return { kind: "needs_decision", who: sig.who ?? target.id, promptId: sig.promptId, prompt: sig.prompt, options: sig.options, answerKind: sig.answerKind };
        }
        throw sig;
      }
      steps.push(...substituted.steps);
      warnings.push(
        `${target.name}: this hit deals 1 STRUCTURE damage instead of its usual damage — no HP damage is rolled or dealt.`,
        ...substituted.warnings,
      );
      // Heat (Self) — the fire cost, owed exactly as it is on any other hit. No Overkill heat to
      // fold in: no damage dice were drawn, so nothing could reroll.
      if (selfHeatAmt > 0) {
        const heatCascade = resolveHeatCascade({ target: attacker, amount: selfHeatAmt, dice, turnOrdinal: state.activation.turnOrdinal, choices: intent.choices, ...interactiveFire(effects), selfCheck: selfCheckContext(state, overlay, { loop: args.events?.loop }) });
        steps.push(...heatCascade.steps);
        warnings.push(...heatCascade.warnings);
      }
      // The notification steps land after the Heat (Self) cascade, in the hit arm's order.
      steps.push(...reactionLinked(notification, attacker.id, target.id));
      warnings.push(...notification.warnings);
      const dealt = substituted.steps.some((s) => s.op === "structure_delta" && s.target === target.id);
      // A structure point carries no damage type, so no `dtype` clause fires on the substitution.
      const settled = settleDamage(reactionCtx, { dealt, dtypes: [] });
      if (isNeedsDecision(settled)) return settled;
      steps.push(...settled.steps);
      warnings.push(...settled.warnings);
      return { steps, warnings, spendReactionBy: undefined, ...(settled.forcedAttacks.length > 0 ? { forcedAttacks: settled.forcedAttacks } : {}), ...(settled.consumedCaps.length > 0 ? { consumedCaps: settled.consumedCaps } : {}) };
    }
  }

  const rolls = rollDamageInstances({
    weapon,
    isCrit,
    dice,
    sharedDamage,
    overrideDamageType,
    resolvedVariableType,
    dtype,
    doubleDamage,
    ...(args.events !== undefined ? { events: args.events } : {}),
  });

  // The window and the settled pass see the attack's damage types (`npc-gaps` 2.6).
  const rolledCtx: ReactionContext = { ...reactionCtx, dtypes: [...new Set(rolls.map((r) => r.dtype))] };

  // ── The reaction interception window (reactive-defenses Phase 1, decisions/0047; relocated
  // by `npc-weapon-rider-damage` Phase 3 decision 7) ──
  // RAW Brace trigger: "you are hit by an attack AND damage has been rolled" — so the window
  // opens HERE, after EVERY instance's damage dice are drawn (and recorded into the input log by
  // step 1.1's RecordingDiceSource) but BEFORE any instance is mitigated/committed — ONE window
  // per attack, not one per instance. The registry offers Brace to an eligible mech target; an
  // unanswered interactive offer throws a `NeedsDecisionSignal` (who = target) → round-trip a
  // prompt. A recorded `brace`/`decline` answer resolves deterministically (replay reproduces
  // it). On `brace` the outcome carries `resistant: true` (folded into `mitigate` below —
  // Resistance halves after Armor, per instance) + the durational `Braced` status step + the
  // defender to spend a reaction for.
  let reaction: ReactionOutcome;
  try {
    reaction = gatherReaction(rolledCtx);
  } catch (sig) {
    if (sig instanceof NeedsDecisionSignal) {
      return {
        kind: "needs_decision",
        who: sig.who ?? target.id,
        promptId: sig.promptId,
        prompt: sig.prompt,
        options: sig.options,
        answerKind: sig.answerKind,
        ...(sig.title !== undefined ? { title: sig.title } : {}),
        ...(sig.summary !== undefined ? { summary: sig.summary } : {}),
        incomingDamage: previewIncomingDamage({ state, target, overlay, weapon, effectiveArmor, ignoreTargetArmor, rolls }),
      };
    }
    throw sig;
  }
  // Hunker Down's bridge (Q3(a)): OR the outcome's Resistance into this window's outcome,
  // BEFORE any instance mitigates — `applyDamageInstance`'s own `resistant` disjunction (below)
  // reads `reaction.resistant` as one of its sources, so this is the one seam that makes a
  // grant on the outcome reach the `damage` step's `resisted` flag. Merge, don't overwrite: a
  // `reaction.resistant` already `true` from THIS window's own Brace/authored-offer gather must
  // not be clobbered by a `false` to-hit bridge.
  if (toHitResistant) reaction = { ...reaction, resistant: true };

  // Split/redirect (`reactive-defenses-content` P2/P3, decision 9; extended per-instance by
  // `npc-weapon-rider-damage` Phase 3 decision 7): a bound partner (the protector) takes part or
  // all of EACH instance's own PRE-mitigation damage; each side then mitigates its own share
  // with its own defenses. The link itself doesn't vary per instance (one reaction window, one
  // outcome), so it's resolved once here; the SHARE math (which depends on each instance's own
  // `preMitigation`) runs inside {@link applyDamageInstance}. Two shapes, mutually exclusive per
  // hit (redirect wins if somehow both are present — not exercised by any P1–P3 content):
  //   - `reaction.split` (Argonaut Shield, automatic, gathered off the TARGET's own overlay):
  //     `ceil(pre/2)` to the protector, the remainder (NEVER a second independent halving — the
  //     double-count trap) stays with the target.
  //   - `reaction.redirect` (Intercede, an offered reaction, gathered off the PROTECTOR's own
  //     overlay via the protector-gather direction): the FULL pre-mitigation total moves to the
  //     protector; the target takes NOTHING.
  // Neither present (the ordinary case) leaves each instance's `targetShare` its full
  // `preMitigation`, unchanged from before this project.
  const protectorLink = reaction.redirect ?? reaction.split;
  const protector = protectorLink ? state.combatants[protectorLink.partnerId] : undefined;

  // Sequential per-instance mitigation + type routing (decisions 3+6): `routeFinalDamage` does the
  // drain-then-cascade (or the heat bypass), once per instance, target share then protector share.
  let overkillHeatTotal = 0;
  // `system-trauma-destruction` Phase 4 (`decisions/0078`): a structure-damage cascade inside
  // this loop can raise a System Trauma "of your choice" prompt (a `NeedsDecisionSignal` naming
  // the TARGET, the SAME Brace transport) — caught here and turned into `needs_decision`,
  // exactly like the reaction-window catch above and the on_hit/on_crit catch in
  // {@link import("./attackTriggerFires").fireHitTriggers}. Nothing has been applied yet (iterate-pure: the re-submitted intent
  // re-runs the whole resolution), so aborting mid-loop on a throw is safe — the partially-built
  // `steps`/`warnings` are simply discarded with the early return.
  // Each instance lands on the target (and protector) as the earlier instances left them, so a
  // second instance past 0 HP cascades into structure like one larger hit (BC-016), and the first
  // lands on what this intent's earlier steps left (`landedOn`, BC-023). Only the damage-taker is
  // folded; every rules read inside the instance keeps the entry `state`.
  // The hit is one damage packet (`event-engine-damage` D1): every instance of the weapon's own
  // damage against this target, raised into the intent's loop once, its cause the outcome.
  const packet: DamagePacketEvent = {
    kind: "damage_packet",
    source: attacker.id,
    target: target.id,
    instances: rolls.map((r) => ({ dtype: r.dtype, amount: r.preMitigation, dice: r.dice, ...(r.sides !== undefined ? { sides: r.sides } : {}), bonus: r.damageBonus, crit: isCrit })),
    fromAttack: true,
    ap: weapon.ap,
  };
  // Armor comes off the packet once (R2, Core p.67: "ARMOR reduces all incoming damage from a
  // single source"), spent across the armorable instances in order: each share's own pool. A
  // Shredded protector has none (Core p.78, R6).
  let armorLeft = weapon.ap ? 0 : effectiveArmor;
  let protectorArmorLeft = weapon.ap || protector === undefined ? 0 : nonAttackArmor({ state, targetId: protector.id, attackerId: attacker.id, overlay, armor: protector.base.armor });
  // Reduce (Inspiring Presence: "reduce damage by −1d6 when taking damage") is one deduction from
  // the packet, after Resistance, spilling across the target's instances in order (Core p.67 step
  // 3, R5). The protector's share takes none of it.
  let reduceLeft = Math.max(0, reaction.reduce ?? 0);
  let landed = args.landedOn ?? state;
  try {
    raiseDamagePacket({
      loop: args.events?.loop,
      packet,
      ...(args.events?.cause !== undefined ? { cause: args.events.cause } : {}),
      land: () => {
        for (const r of rolls) {
          overkillHeatTotal += r.overkillHeat;
          const applied = applyDamageInstance({
            state,
            target: landed.combatants[target.id] ?? target,
            intent,
            weapon,
            overlay,
            effects,
            dice,
            armor: armorLeft,
            protectorArmor: protectorArmorLeft,
            reduce: reduceLeft,
            ignoreTargetArmor,
            isCrit,
            r,
            reaction,
            protector: protector && (landed.combatants[protector.id] ?? protector),
            ...(args.events?.loop !== undefined ? { loop: args.events.loop } : {}),
          });
          armorLeft -= applied.armorUsed;
          protectorArmorLeft -= applied.protectorArmorUsed;
          reduceLeft -= applied.reduced;
          landed = applyResolved(landed, applied.steps);
          steps.push(...applied.steps);
          warnings.push(...applied.warnings);
        }
      },
    });
  } catch (sig) {
    if (sig instanceof NeedsDecisionSignal) {
      return { kind: "needs_decision", who: sig.who ?? target.id, promptId: sig.promptId, prompt: sig.prompt, options: sig.options, answerKind: sig.answerKind };
    }
    throw sig;
  }

  // Heat lands on the **wielder** (the attacker), not the target, CASCADING into the
  // Overheating table when it pushes the wielder over its HEAT CAP (A3). Overkill's
  // reroll-driven heat (now summed across EVERY instance's own reroll pass — decision 5) and
  // Heat (Self)'s flat per-fire cost are DIFFERENT mechanisms that can both apply to the same
  // weapon — folded into ONE combined amount so they share a single `resolveHeatCascade` call
  // (see `selfHeatAmt`'s doc comment on {@link ApplyHitDamageArgs} for why two separate calls
  // would mis-cascade a combined cap-cross).
  if (overkillHeatTotal + selfHeatAmt > 0) {
    const heatCascade = resolveHeatCascade({ target: attacker, amount: overkillHeatTotal + selfHeatAmt, dice, turnOrdinal: state.activation.turnOrdinal, choices: intent.choices, ...interactiveFire(effects), selfCheck: selfCheckContext(state, overlay, { loop: args.events?.loop }) });
    steps.push(...heatCascade.steps);
    warnings.push(...heatCascade.warnings);
  }

  // The Brace consequence (reactive-defenses Phase 1): splice the durational +1-Difficulty
  // `Braced` status step, and mark the defender whose reaction the room must spend (the sibling
  // `spend_reaction` folded into the attacker's batch). Empty / undefined when nothing braced.
  // ONE reaction window for the whole attack (decision 7) — this fires once, not per instance.
  // Its own `reaction` link (`chained-resolution-sequence` 4.3), so the receipt and the map name
  // the reactor; the settled pass below hands back to the attacker.
  steps.push(...reactionLinked(reaction, attacker.id, (reaction.redirect ?? reaction.split)?.partnerId ?? target.id));
  // `npc-features-fielded-roster` Phase 7.4 — an authored offer's own `advisory` atom (Rebound's
  // reflection warning) rides `reaction.warnings`, not a grounded step; fold it into the same
  // `Resolved.warnings` channel every other advisory in this resolution already uses.
  if (reaction.warnings) warnings.push(...reaction.warnings);

  // ── The settled-damage pass (`npc-features-fielded-roster` Phase 13.2, Open fork F3) ──
  // Every instance is mitigated now, so "did the target take damage" is a fact: its own final
  // damage above 0, summed over its non-immune `damage` steps. Clauses that match on `dealt`
  // (Aggression) run here and nowhere earlier; a fully absorbed hit reads `dealt: false`.
  const dealtSteps = steps.filter((s): s is DamageStep => s.op === "damage" && s.target === target.id && s.immune !== true && s.final > 0);
  const settled = settleDamage(rolledCtx, { dealt: dealtSteps.length > 0, dtypes: [...new Set(dealtSteps.map((s) => s.dtype))] });
  if (isNeedsDecision(settled)) return settled;
  steps.push(...settled.steps);
  warnings.push(...settled.warnings);

  // The reaction window's forced attacks (Rebound's reflection) run before the settled pass's
  // (Aggression's), in the order the rules text reads them.
  const forcedAttacks = [...(reaction.forcedAttacks ?? []), ...settled.forcedAttacks];
  // RAW Brace: Resistance to "all damage, heat, and burn from the triggering attack", so heat an
  // on-hit clause adds halves with the damage (Shredded leaves no Resistance from any source).
  const heatResisted = !ignoreTargetArmor && (reaction.resistant || foldedResistance({ state, targetId: target.id, attackerId: intent.attacker, fromAttack: true, dtype: "heat", overlay }));
  const targetHeatHalvings = (heatResisted ? 1 : 0) + (intent.damageScale === "half" ? 1 : 0);
  const targetReactionResisted = !ignoreTargetArmor && reaction.resistant;
  return {
    steps,
    warnings,
    spendReactionBy: reaction.spendReactionBy,
    reaction,
    ...(forcedAttacks.length > 0 ? { forcedAttacks } : {}),
    ...(settled.consumedCaps.length > 0 ? { consumedCaps: settled.consumedCaps } : {}),
    ...(targetHeatHalvings > 0 ? { targetHeatHalvings } : {}),
    ...(targetReactionResisted ? { targetReactionResisted } : {}),
  };
}

/** What {@link applyReliableFloorDamage} settles for the rest of the spine. */
export type ReliableFloorApplication = {
  /**
   * The floor's `damage` step, its routed cascade, then the Heat (Self) cascade — the caller
   * pushes them onto its own accumulator in one go at the seam, byte-identically to the inline
   * pushes, followed by whatever the target's `on:"damage"` clauses and accepted offers emitted.
   */
  steps: ResolvedStep[];
  /** The same, for the advisory warnings the immune note and the cascades raised. */
  warnings: string[];
  /** The reactor whose reaction an accepted damage-window offer spent (Rebound), for the caller's `reactionSpentBy`. */
  spendReactionBy?: { combatantId: string; name: string }[];
  /** Forced attacks an accepted offer (Rebound's reflection) and the settled clauses (Aggression) queued, for the referee. */
  forcedAttacks?: ForcedAttackRequest[];
  /** The cap uses the settled-damage pass's clauses consumed (BC-012). */
  consumedCaps?: { carrier: string; key: string }[];
};

/** Args for {@link applyReliableFloorDamage} (max-params-3 discipline, `CODE_STYLE.md`). */
export type ApplyReliableFloorDamageArgs = {
  state: EncounterState;
  /** The wielder — the Heat (Self) cascade lands on it, never on the target. */
  attacker: Combatant;
  target: Combatant;
  intent: RequestAttack;
  /** The SELECTED firing profile — its `reliable` is the floor and its `ap` the mitigation input. */
  weapon: WeaponProfile;
  overlay: LoadedOverlay | undefined;
  effects: EffectsConfig | undefined;
  dice: DiceSource;
  /** The state the floor lands on, as {@link ApplyHitDamageArgs.landedOn}. Omitted → `state`. */
  landedOn?: EncounterState;
  /** S4's settled damage type. The floor is a single instance, so there is no per-instance type here. */
  dtype: DamageType;
  /**
   * The chain preamble's Exposed verdict — `overlayDoubleDamage && isPhysical(dtype)`, already
   * resolved against THIS attack's one type. Passed settled rather than re-derived, because the
   * floor has no per-instance types to re-derive it against (which is why {@link ApplyHitDamageArgs}
   * takes the raw facet instead).
   */
  doublePhysical: boolean;
  /** S3's Shredded facet — no armor, and no Resistance from any source. */
  ignoreTargetArmor: boolean;
  /** The chain preamble's effective target armor (0 when Shredded or AP flattened it). */
  effectiveArmor: number;
  /** S8's Heat (Self) per-fire cost — charged on a miss too; firing is what costs the heat. */
  selfHeatAmt: number;
  /** The intent's event loop: the floor's and the Heat (Self) cascade's rolls raise into it. */
  loop: EventLoop | undefined;
  /**
   * The already-computed `rangedAttack` fact, threaded onto the notification pass's
   * {@link ReactionContext.ranged} so a `match: { family: "damage", ranged: false }` clause
   * (Spined Monstrosity's riposte) gates on it exactly as it does on a hit.
   */
  rangedAttack: boolean;
  /** The already-computed `techAttack` fact, threaded onto {@link import("../reactionRegistry").ReactionContext.tech} beside `rangedAttack`. */
  techAttack: boolean;
  /** Combatants that already spent a reaction earlier in THIS resolution — see {@link ApplyHitDamageArgs.spentReactions}. */
  spentReactions?: ReadonlySet<string>;
  /**
   * The Unraveler's discarded hit's damage-window outcome ({@link HitDamageApplication.reaction}):
   * the floor takes it in place of its own miss-side window, so a Brace taken against the hit stays
   * taken (its reaction spent, `Braced` planted) and its Resistance halves the floor.
   */
  carriedReaction?: ReactionOutcome;
};

/**
 * **S9c — apply a Reliable weapon's floor damage on a miss.** See the module doc for how this
 * arm differs from the hit arm, and for the single exit's recipient.
 */
export function applyReliableFloorDamage(args: ApplyReliableFloorDamageArgs): ReliableFloorApplication | NeedsDecision {
  const { state, attacker, target, intent, weapon, overlay, effects, dice } = args;
  const { dtype, doublePhysical, ignoreTargetArmor, effectiveArmor, selfHeatAmt, rangedAttack, techAttack, loop } = args;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  // Built once and shared by the damage window and the settled pass. `hit: false` keeps a
  // "hits you" clause (Return Fire) off the miss; a forced reflection opens no offer.
  const reactionCtx: ReactionContext = {
    state,
    attacker,
    target,
    choices: intent.choices,
    ...interactiveFire(effects),
    dice,
    overlay,
    ranged: rangedAttack,
    tech: techAttack,
    hit: false,
    dtypes: [dtype],
    ...(args.spentReactions ? { spentReactions: args.spentReactions } : {}),
    triggeringAttack: intent,
    ...(effects?.forced?.reactions === false ? { offersClosed: true } : {}),
  };

  // Reliable N: a **miss** still deals N damage (the floor). Still typed and
  // mitigated (armor/AP/resist apply to the N), per the rulebook (Reliable sets
  // a minimum *damage*, which then mitigates normally). No dice rolled — the
  // floor is fixed — so `dice` is empty and `bonus` carries N as the pre-mit base.
  const preMitigation = doublePhysical ? weapon.reliable * 2 : weapon.reliable;

  // The floor is damage from the attack, so the target's authored damage-window offers open on it
  // (Rebound: "takes damage from a ranged attack"), before mitigation, as on a hit. Brace does not
  // (RAW "hit by an attack"), nor do protector offers or Argonaut Shield's split
  // (`gatherReliableMissReaction`). The same call runs the automatic `on:"damage"` clauses.
  let reaction: ReactionOutcome;
  try {
    reaction = args.carriedReaction ?? gatherReliableMissReaction(reactionCtx);
  } catch (sig) {
    if (sig instanceof NeedsDecisionSignal) {
      return {
        kind: "needs_decision",
        who: sig.who ?? target.id,
        promptId: sig.promptId,
        prompt: sig.prompt,
        options: sig.options,
        answerKind: sig.answerKind,
        ...(sig.title !== undefined ? { title: sig.title } : {}),
        ...(sig.summary !== undefined ? { summary: sig.summary } : {}),
        incomingDamage: previewIncomingDamage({ state, target, overlay, weapon, effectiveArmor, ignoreTargetArmor, rolls: [{ dtype, preMitigation }] }),
      };
    }
    throw sig;
  }

  // A standing Resistance grant (Argonaut Shield, etc.) folds here as on a hit, ORed with an
  // accepted offer's (Rebound's 4+). `status-catalog-completion` P1.3: Shredded suppresses both
  // (RAW p.78).
  const reliableResistant = !ignoreTargetArmor && (reaction.resistant || foldedResistance({ state, targetId: target.id, attackerId: attacker.id, fromAttack: true, dtype, overlay }));
  const reliableImmune = immuneToDamage({ state, targetId: target.id, dtype, overlay });
  // An accepted damage-reduce offer (Leadership Die) subtracts after mitigation, as on a hit.
  const reduceAmt = reaction.reduce ?? 0;
  // The floor is one damage packet of one flat instance (`event-engine-damage` 2.3), landed through
  // the pipeline's per-instance core with the attack's own facts, as a hit's instances are. No dice
  // are rolled — the floor is fixed — so `dice` is empty and `bonus` carries N as the pre-mit base.
  // Reliable's floor is still DAMAGE — the same type-routed split the hit branch runs applies to it
  // (a heat-typed Reliable floor cascades straight to the heat track instead of Overshield/HP). It
  // can ALSO raise a System Trauma prompt (`system-trauma-destruction` Phase 4) on a near-dead mech
  // — caught the same way the hit branch's own instance loop catches it.
  let floor: LandedInstance;
  try {
    floor = raiseDamagePacket({
      loop,
      packet: { kind: "damage_packet", source: attacker.id, target: target.id, instances: [{ dtype, amount: preMitigation, dice: [], bonus: weapon.reliable }], fromAttack: true, ap: weapon.ap },
      land: () =>
        landDamageInstance({
          state,
          target: args.landedOn?.combatants[target.id] ?? target,
          instance: { dtype, amount: preMitigation, dice: [], bonus: weapon.reliable, crit: false },
          ap: weapon.ap,
          armor: weapon.ap ? 0 : effectiveArmor,
          resistant: reliableResistant,
          reduce: reduceAmt,
          immune: reliableImmune,
          scale: intent.damageScale,
          // Provenance: Reliable's floor is still the firing weapon's own damage.
          witness: { source: { kind: "weapon", name: weapon.name, id: weapon.id } },
          route: { dice, choices: intent.choices, ...interactiveFire(effects), selfCheck: selfCheckContext(state, overlay, { loop }), from: attacker.id },
        }),
    });
  } catch (sig) {
    if (sig instanceof NeedsDecisionSignal) {
      return { kind: "needs_decision", who: sig.who ?? target.id, promptId: sig.promptId, prompt: sig.prompt, options: sig.options, answerKind: sig.answerKind };
    }
    throw sig;
  }
  const reliableFinal = floor.witness.final;
  steps.push(floor.witness);
  if (reliableImmune) {
    warnings.push(`${target.name}: IMMUNE to ${dtype} damage — no effect (RAW p.68, never takes damage from an external source).`);
  } else if (floor.reduced > 0) {
    warnings.push(`${target.name}: incoming damage reduced by ${floor.reduced} (Inspiring Presence) — ${floor.mitigated} → ${reliableFinal}.`);
  }
  steps.push(...floor.landing);
  warnings.push(...floor.warnings);
  // Heat (Self) still applies on a Reliable-floor miss — firing the weapon is what
  // costs the heat, independent of whether the Reliable floor even landed damage.
  if (selfHeatAmt > 0) {
    const heatCascade = resolveHeatCascade({ target: attacker, amount: selfHeatAmt, dice, turnOrdinal: state.activation.turnOrdinal, choices: intent.choices, ...interactiveFire(effects), selfCheck: selfCheckContext(state, overlay, { loop }) });
    steps.push(...heatCascade.steps);
    warnings.push(...heatCascade.warnings);
  }
  // The window's steps and warnings (the automatic clauses', then any accepted offer's), then
  // the settled pass, in the hit arm's order. Its own `reaction` link, like the hit arm's (4.3).
  steps.push(...reactionLinked(reaction, attacker.id, (reaction.redirect ?? reaction.split)?.partnerId ?? target.id));
  if (reaction.warnings) warnings.push(...reaction.warnings);
  const reliableDealt = !reliableImmune && reliableFinal > 0;
  const settled = settleDamage(reactionCtx, { dealt: reliableDealt, dtypes: reliableDealt ? [dtype] : [] });
  if (isNeedsDecision(settled)) return settled;
  steps.push(...settled.steps);
  warnings.push(...settled.warnings);

  // The window's forced attacks (Rebound's reflection) run before the settled pass's (Aggression's).
  const forcedAttacks = [...(reaction.forcedAttacks ?? []), ...settled.forcedAttacks];
  return {
    steps,
    warnings,
    ...(reaction.spendReactionBy ? { spendReactionBy: reaction.spendReactionBy } : {}),
    ...(forcedAttacks.length > 0 ? { forcedAttacks } : {}),
    ...(settled.consumedCaps.length > 0 ? { consumedCaps: settled.consumedCaps } : {}),
  };
}
